import API from "utils/API";
import authHeader from "utils/auth-header";
import { IDriver } from "../domain/IDriver";
import { IOutput } from "../domain/IOutput";
import { IOutputListItemSearch } from "../domain/IOutputListItemSearch";
import { IOutputsResponse } from "../domain/IOutputsResponse";
import { IRepositoryDetails } from "../domain/IRepositoryDetails";
import { IResponseDrivers } from "../domain/IResponseDrivers";
import { IResponseRepositories } from "../domain/IResponseRepositories";
import { IResponseStockItems } from "../domain/IResponseStockItems";
import { IResponseVehicles } from "../domain/IResponseVehicles";
import { IStock } from "../domain/IStock";
import { IStockInputCreate } from "../domain/IStockInputCreate";
import { IStockInputsResponse } from "../domain/IStockInputsResponse";
import { IStockItem } from "../domain/IStockItem";
import { IStockOutputForProjectOrPartnerCreate } from "../domain/IStockOutputForProjectOrPartnerCreate";
import { IVehicle } from "../domain/IVehicle";
import { IGetStockInputsRequestParams } from "../domain/IGetStockInputsRequestParams";
import { getQeuryParams } from "utils/queryParamsUtil";
import { ICreateReturnOutput } from "../domain/ICreateReturnOutput";
import { IStockInputUpdate } from "../domain/IStockInputUpdate";
import { IStockInput } from "../domain/IStockInput";
import { IStockOutputUpdate } from "../domain/IStockOutputUpdate";
import { IRepositorySimple } from "../domain/IRepositorySimple";
import { IStockOutputForEmployeeCreate } from "../domain/IStockOutputForEmployeeCreate";
import { IGetPageRequestParams } from "hooks/pagingAndSort";
import { IStockReservationCreate } from "../domain/IStockReservationCreate";
import { IStockReservations } from "../domain/IStockReservations";
import { IStockReservationUpdate } from "../domain/IStockReservationUpdate";
import { IStockReservation } from "../domain/IStockReservation";
import { IMessage } from "domain/IMessage";
import { IReturnOutputResult } from "../domain/IReturnOutputResult";
import { IUpdateOutputComand } from "../domain/IUpdateOutputCommand";
import { IUpdateOutputItemsResult } from "../domain/IUpdateOutputItemsResult";

const getRepositoriesForStock = async (
  stock: string
): Promise<IRepositorySimple[]> => {
  var headers = authHeader();
  try {
    const response = await API.get<IResponseRepositories>(
      `/stock/public/stock/${stock}/repository`,
      { headers }
    );
    let repositories = response.data.repositories;
    return repositories;
  } catch (err) {
    throw err;
  }
};

const getRepositoryForStock = async (
  stock: string,
  repositoryId: number
): Promise<IRepositoryDetails> => {
  var headers = authHeader();
  try {
    const response = await API.get<IRepositoryDetails>(
      `/stock/public/stock/${stock}/repository/${repositoryId}`,
      {
        headers,
      }
    );
    let repositories = response.data;
    return repositories;
  } catch (err) {
    throw err;
  }
};

const createRepositoryForStock = async (
  stock: string,
  repositoryName: string
): Promise<IRepositoryDetails> => {
  var headers = authHeader();
  try {
    const response = await API.post<IRepositoryDetails>(
      `/stock/public/stock/${stock}/repository`,
      { repositoryName },
      { headers }
    );
    return response.data;
  } catch (err) {
    throw err;
  }
};

const createStock = async (stockName: string): Promise<IStock> => {
  var headers = authHeader();
  try {
    const response = await API.post<IStock>(
      "/stock/public/stock",
      { stockName },
      { headers }
    );
    return response.data;
  } catch (err) {
    throw err;
  }
};

const getStockInputs = async (
  getInputsRequestParams: IGetStockInputsRequestParams,
  pagingData: IGetPageRequestParams,
  signal: AbortSignal
): Promise<IStockInputsResponse> => {
  var headers = authHeader();
  const { stock, ...rest } = getInputsRequestParams;
  try {
    const response = await API.get<IStockInputsResponse>(
      `/stock/public/stock/${stock}/input${getQeuryParams({ ...rest, ...pagingData })}`,
      { headers, signal }
    );
    return response.data;
  } catch (err) {
    throw err;
  }
};

const createStockInput = async (
  stock: string,
  data: IStockInputCreate
): Promise<IStockInput> => {
  var headers = authHeader();
  try {
    const response = await API.post<IStockInput>(
      `/stock/public/stock/${stock}/input`,
      data,
      { headers }
    );
    return response.data;
  } catch (err) {
    throw err;
  }
};

const getStockInputItems = async (): Promise<IStockItem[]> => {
  var headers = authHeader();
  try {
    const response = await API.get<IResponseStockItems>(
      `/stock/public/stock/input/item`,
      { headers }
    );
    return response.data.items;
  } catch (err) {
    throw err;
  }
};

const createStockInputItem = async (itemName: string): Promise<IStockItem> => {
  var headers = authHeader();
  try {
    const response = await API.post<IStockItem>(
      `/stock/public/stock/input/item`,
      { itemName },
      { headers }
    );
    return response.data;
  } catch (err) {
    throw err;
  }
};

const getDrivers = async (): Promise<IDriver[]> => {
  var headers = authHeader();
  try {
    const response = await API.get<IResponseDrivers>(
      "stock/public/stock/driver",
      { headers }
    );
    return response.data.drivers;
  } catch (err) {
    throw err;
  }
};

const createDriver = async (name: string): Promise<IDriver> => {
  var headers = authHeader();
  try {
    const response = await API.post<IDriver>(
      "/stock/public/stock/driver",
      { name },
      { headers }
    );
    return response.data;
  } catch (err) {
    throw err;
  }
};

const getVehicles = async (): Promise<IVehicle[]> => {
  var headers = authHeader();
  try {
    const response = await API.get<IResponseVehicles>(
      "/stock/public/stock/vehicle",
      { headers }
    );
    return response.data.vehicles;
  } catch (err) {
    throw err;
  }
};

const createVehicle = async (registrationPlate: string): Promise<IVehicle> => {
  var headers = authHeader();
  try {
    const response = await API.post<IVehicle>(
      "/stock/public/stock/vehicle",
      { registrationPlate },
      { headers }
    );
    return response.data;
  } catch (err) {
    throw err;
  }
};

const createOutputPartner = async (
  stockName: string,
  output: IStockOutputForProjectOrPartnerCreate
): Promise<IOutput> => {
  var headers = authHeader();
  try {
    const response = await API.post<IOutput>(
      `/stock/public/stock/${stockName}/output-for-partner`,
      output,
      { headers }
    );
    return response.data;
  } catch (err) {
    throw err;
  }
};

const createOutputProject = async (
  stockName: string,
  output: IStockOutputForProjectOrPartnerCreate
): Promise<IOutput> => {
  var headers = authHeader();
  try {
    const response = await API.post<IOutput>(
      `/stock/public/stock/${stockName}/output-for-project`,
      output,
      { headers }
    );
    return response.data;
  } catch (err) {
    throw err;
  }
};

const createOutputEmployee = async (
  stockName: string,
  output: IStockOutputForEmployeeCreate
): Promise<IOutput> => {
  var headers = authHeader();
  try {
    const response = await API.post<IOutput>(
      `/stock/public/stock/${stockName}/output-for-employee`,
      output,
      { headers }
    );
    return response.data;
  } catch (err) {
    throw err;
  }
};

const getOutputs = async (
  stock: string,
  search: IOutputListItemSearch,
  pagingData: IGetPageRequestParams,
  signal: AbortSignal
): Promise<IOutputsResponse> => {
  var headers = authHeader();
  try {
    const response = await API.get<IOutputsResponse>(
      `/stock/public/stock/${stock}/output${getQeuryParams({ ...search, ...pagingData })}`,
      { headers, signal }
    );
    return response.data;
  } catch (err) {
    throw err;
  }
};

const getOutput = async (
  outputId: number
): Promise<IOutput> => {
  var headers = authHeader();
  try {
    const response = await API.get<IOutput>(
      `/stock/public/stock/output/${outputId}`,
      { headers }
    );
    return response.data;
  } catch (err) {
    throw err;
  }
};

const returnOutput = async (
  outputId: number,
  params: ICreateReturnOutput
): Promise<IReturnOutputResult> => {
  var headers = authHeader();
  try {
    const response = await API.post<IReturnOutputResult>(
      `/stock/public/stock/output/${outputId}/return`,
      params,
      { headers }
    );
    return response.data;
  } catch (err) {
    throw err;
  }
};

const updateOutputItems = async (
  command: IUpdateOutputComand
): Promise<IUpdateOutputItemsResult> => {
  var headers = authHeader();
  const { outputId, items } = command;
  try {
    const response = await API.put<IUpdateOutputItemsResult>(
      `/stock/public/stock/output/${outputId}`,
      { items },
      { headers }
    );
    return response.data;
  } catch (err) {
    throw err;
  }
};

const createReservation = async (
  reservationRequest: IStockReservationCreate
): Promise<IStockReservation> => {
  var headers = authHeader();
  try {
    const response = await API.post<IStockReservation>(
      `/stock/public/stock/reservation`,
      reservationRequest,
      { headers }
    );
    return response.data;
  } catch (err) {
    throw err;
  }
};

const updateReservation = async (
  reservationRequest: IStockReservationUpdate
): Promise<IStockReservation> => {
  var headers = authHeader();
  try {
    const { reservationId, ...rest } = reservationRequest;
    const response = await API.put<IStockReservation>(
      `/stock/public/stock/reservation/${reservationId}`,
      rest,
      { headers }
    );
    return response.data;
  } catch (err) {
    throw err;
  }
};

const deleteReservation = async (
  reservationId: number
): Promise<IMessage> => {
  var headers = authHeader();
  try {
    const response = await API.delete<IMessage>(
      `/stock/public/stock/reservation/${reservationId}`,
      { headers }
    );
    return response.data;
  } catch (err) {
    throw err;
  }
};

const getReservations = async (): Promise<IStockReservations> => {
  var headers = authHeader();
  try {
    const response = await API.get<IStockReservations>(
      `/stock/public/stock/reservation`,
      { headers }
    );
    return response.data;
  } catch (err) {
    throw err;
  }
};

const updateStockInput = async (
  data: IStockInputUpdate
): Promise<IStockInput[]> => {
  var headers = authHeader();
  try {
    const { inputId, ...rest } = data;
    const response = await API.put<IStockInput[]>(
      `/stock/public/stock/input/${inputId}`,
      { ...rest },
      { headers }
    );
    let stockInput = response.data;
    return stockInput;
  } catch (err) {
    throw err;
  }
};

const updateStockOutput = async (
  data: IStockOutputUpdate
): Promise<IOutput> => {
  var headers = authHeader();
  try {
    const { outputId, outputItemId, ...rest } = data;
    const response = await API.put<IOutput>(
      `/stock/public/stock/output/${outputId}/${outputItemId}`,
      { ...rest },
      { headers }
    );
    let stockInput = response.data;
    return stockInput;
  } catch (err) {
    throw err;
  }
};

const deleteStockInput = async (
  inputId: number
): Promise<IMessage> => {
  var headers = authHeader();
  try {
    const response = await API.delete<IMessage>(
      `/stock/public/input/${inputId}`,
      { headers }
    );
    return response.data;
  } catch (err) {
    throw err;
  }
};

const deleteStockOutput = async (
  outputId: number
): Promise<{ deletedOutputIds: number[] }> => {
  var headers = authHeader();
  try {
    const response = await API.delete<{ deletedOutputIds: number[] }>(
      `/stock/public/output/${outputId}`,
      { headers }
    );
    return response.data;
  } catch (err) {
    throw err;
  }
};


export const stockService = {
  getRepositoriesForStock,
  getRepositoryForStock,
  createRepositoryForStock,
  createStock,
  getStockInputs,
  createStockInput,
  getStockInputItems,
  createStockInputItem,
  getDrivers,
  createDriver,
  getVehicles,
  createVehicle,
  createOutputPartner,
  createOutputProject,
  createOutputEmployee,
  getOutputs,
  getOutput,
  returnOutput,
  updateOutputItems,
  createReservation,
  updateReservation,
  deleteReservation,
  getReservations,
  updateStockInput,
  updateStockOutput,
  deleteStockInput,
  deleteStockOutput,
};
